import { useLazyQuery, type QueryResult } from '@apollo/client'
import { graphql } from '~publish/graphql'
import type {
  CommentsScoreResponse,
  CommentsScoreInput,
} from '~publish/gql/graphql'

export const commentsScoreQuery = graphql(/* GraphQL */ `
  query CommentsScoreQuery($input: CommentsScoreInput!) {
    commentsScore(input: $input) {
      currentAchievementLevel
      currentWeekScore {
        totalValue
        responseRate {
          value
          percentage
        }
        responseSpeed {
          value
          percentage
        }
        consistency {
          value
          percentage
        }
      }
      previousWeekScore {
        totalValue
        responseRate {
          value
          percentage
        }
        responseSpeed {
          value
          percentage
        }
        consistency {
          value
          percentage
        }
      }
    }
  }
`)

type CommentsScoreData = {
  commentsScore: CommentsScoreResponse
}

type UseCommentsScoreQueryReturn = {
  fetchCommentsScore: (params: {
    organizationId: string
    channelId: string
  }) => Promise<QueryResult<CommentsScoreData, { input: CommentsScoreInput }>>
}

/**
 * Hook to fetch the comments score for a given organization and channel.
 * @returns {UseCommentsScoreQueryReturn} An object containing the fetchCommentsScore function
 */
export function useCommentsScoreQuery(): UseCommentsScoreQueryReturn {
  const [getCommentsScore] = useLazyQuery<
    CommentsScoreData,
    { input: CommentsScoreInput }
  >(commentsScoreQuery)

  const fetchCommentsScore = async (params: {
    organizationId: string
    channelId: string
  }): Promise<
    QueryResult<CommentsScoreData, { input: CommentsScoreInput }>
  > => {
    const { organizationId, channelId } = params

    return await getCommentsScore({
      variables: {
        input: {
          organizationId,
          channelId,
        },
      },
      fetchPolicy: 'no-cache',
    })
  }

  return { fetchCommentsScore }
}
